import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Company, Site, Inspection } from "../../../api";
import { drawPill } from "../../Shared/pdfCharts";

const companyCtrl = new Company();
const siteCtrl    = new Site();
const inspCtrl    = new Inspection();

// Mismo ícono que usa el header de la app (layouts/AdminLayout/AdminLayout.js,
// import logoIcon from "assets/svg/isosustemia 2.svg") — acá se necesita en
// PNG (no SVG) porque doc.addImage no soporta SVG directo sin svg2pdf.js.
const LOGO_URL = `${process.env.PUBLIC_URL}/isosustemia 2.png`;

async function loadLogoDataUrl() {
  try {
    const res  = await fetch(LOGO_URL);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function shortId(id) {
  return `INSP-${(id || "").slice(-6).toUpperCase()}`;
}

function formatDate(d) {
  if (!d) return "-";
  const str = typeof d === "string" ? d : new Date(d).toISOString();
  const [y, m, day] = str.split("T")[0].split("-");
  return `${day}/${m}/${y}`;
}

function initialsOf(name) {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

async function photoToDataUrl(accessToken, key) {
  try {
    const blob = await inspCtrl.getFileBlob(accessToken, key);
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Informe de una inspección puntual: ítem por ítem, hallazgos con foto
// "antes" (la del ítem) / "después" (pendiente — el cierre de hallazgos con
// evidencia posterior todavía no existe como funcionalidad) y firma.
// A diferencia del export de fila simple (exportRows), acá se arma un
// documento nativo con jsPDF (no una tabla plana), pensado para imprimir o
// adjuntar en el correo de la fila.
export async function generateInspectionReportPdf({ inspection, accessToken, company }) {
  // Industria del SITIO de esta inspección (no la del sitio activo en el
  // encabezado — pueden diferir si se exporta un registro de otro sitio).
  const [companyDoc, siteDoc, siteProfile, logoDataUrl] = await Promise.all([
    company && company !== "0" ? companyCtrl.getCompany(accessToken, company).catch(() => null) : Promise.resolve(null),
    inspection.site?._id ? siteCtrl.getSite(accessToken, inspection.site._id).catch(() => null) : Promise.resolve(null),
    inspection.site?._id ? inspCtrl.getSiteIndustryProfile(accessToken, inspection.site._id).catch(() => null) : Promise.resolve(null),
    loadLogoDataUrl(),
  ]);
  const siteIndustry = siteProfile?.industry || null;

  const responses    = inspection.responses || [];
  const compliant    = responses.filter((r) => r.status === "compliant").length;
  const nonCompliant = responses.filter((r) => r.status === "non_compliant").length;
  const na           = responses.filter((r) => r.status === "na").length;
  const hallazgos    = inspection.hallazgos || [];
  const accionesCount = hallazgos.filter((h) => h.actionPlanId).length;

  const responsesByItem = {};
  responses.forEach((r) => { responsesByItem[String(r.itemId)] = r; });

  // Foto "antes" = la primera foto cargada en el ítem que originó el hallazgo.
  const hallazgoPhotos = await Promise.all(
    hallazgos.map((h) => {
      const r = h.itemId ? responsesByItem[String(h.itemId)] : null;
      const key = r?.photos?.[0];
      return key ? photoToDataUrl(accessToken, key) : Promise.resolve(null);
    })
  );

  const registryId = shortId(inspection._id);
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const margin  = 40;
  const pageW   = doc.internal.pageSize.getWidth();
  const pageH   = doc.internal.pageSize.getHeight();
  const contentW = pageW - margin * 2;

  // ── Encabezado ────────────────────────────────────────────────────────────
  let y = margin;
  doc.setFontSize(15);
  doc.setFont(undefined, "bold");
  doc.setTextColor(20, 40, 80);
  doc.text("Informe de Inspección — Check List", margin, y);

  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.setTextColor(26, 28, 66);
  const brandText = "sustemia";
  const brandTextW = doc.getTextWidth(brandText);
  const logoSize = 14;
  const logoGap  = 5;
  const brandGroupW = logoDataUrl ? logoSize + logoGap + brandTextW : brandTextW;
  const brandX = pageW - margin - brandGroupW;
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", brandX, y - logoSize + 3, logoSize, logoSize);
    } catch { /* si falla, se sigue solo con el texto */ }
  }
  doc.text(brandText, logoDataUrl ? brandX + logoSize + logoGap : brandX, y);

  y += 16;
  doc.setFontSize(10.5);
  doc.setFont(undefined, "normal");
  doc.setTextColor(90, 90, 90);
  doc.text(inspection.template?.name || "-", margin, y);

  y += 10;
  doc.setDrawColor(20, 40, 80);
  doc.setLineWidth(1.2);
  doc.line(margin, y, pageW - margin, y);
  doc.setLineWidth(0.6);

  y += 26;
  const compName = companyDoc?.name || "-";
  doc.setFillColor(230, 234, 240);
  doc.roundedRect(margin, y - 12, 26, 26, 4, 4, "F");
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.setTextColor(90, 100, 120);
  doc.text(initialsOf(compName) || "-", margin + 13, y + 3, { align: "center" });

  doc.setFontSize(11);
  doc.setTextColor(20, 40, 80);
  doc.text(compName, margin + 36, y - 2);

  doc.setFontSize(8.5);
  doc.setFont(undefined, "normal");
  doc.setTextColor(110, 110, 110);
  const cuitLine = [companyDoc?.cuit ? `ID Fiscal: ${companyDoc.cuit}` : null, siteDoc?.country].filter(Boolean).join(" · ");
  doc.text(cuitLine || "-", margin + 36, y + 10);
  const addrLine = [siteDoc?.address, siteDoc?.name || inspection.site?.name].filter(Boolean).join(" · ");
  doc.text(addrLine || "-", margin + 36, y + 21);

  y += 42;

  // ── Metadata (industria / registro / fecha / inspector) ───────────────────
  const inspectorName = [inspection.inspector?.firstname, inspection.inspector?.lastname].filter(Boolean).join(" ");
  const metaFields = [
    { label: "INDUSTRIA",   value: siteIndustry?.name || "-" },
    { label: "REGISTRO N°", value: registryId },
    { label: "FECHA",       value: formatDate(inspection.date) },
    { label: "INSPECTOR",   value: inspection.signature?.name || inspectorName || "-" },
  ];
  const metaColW = contentW / metaFields.length;
  metaFields.forEach((f, i) => {
    const x = margin + i * metaColW;
    doc.setFontSize(7.5);
    doc.setFont(undefined, "bold");
    doc.setTextColor(140, 140, 140);
    doc.text(f.label, x, y);
    doc.setFontSize(10.5);
    doc.setFont(undefined, "bold");
    doc.setTextColor(20, 40, 80);
    doc.text(String(f.value), x, y + 14);
  });

  y += 36;

  // ── KPIs ────────────────────────────────────────────────────────────────
  const kpis = [
    { label: "Ítems evaluados",         value: String(responses.length) },
    { label: "% Cumplimiento",          value: `${inspection.cumplimiento ?? 0}%` },
    { label: "Hallazgos (No)",          value: String(nonCompliant) },
    { label: "Acciones (Plan Acción)",  value: String(accionesCount) },
  ];
  const kpiColW = contentW / kpis.length;
  kpis.forEach((k, i) => {
    const x = margin + i * kpiColW;
    doc.setFontSize(7.5);
    doc.setFont(undefined, "bold");
    doc.setTextColor(140, 140, 140);
    doc.text(k.label.toUpperCase(), x, y);
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.setTextColor(30, 100, 60);
    doc.text(k.value, x, y + 20);
  });

  y += 40;
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.setTextColor(20, 40, 80);
  doc.text("Detalle del checklist", margin, y);
  y += 8;

  // ── Tabla de ítems ──────────────────────────────────────────────────────
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [["#", "CATEGORÍA", "ÍTEM VERIFICADO", "RESULT."]],
    body: responses.map((r, i) => [
      String(i + 1),
      r.category || "General",
      r.question || "-",
      r.status === "compliant" ? "Sí" : r.status === "non_compliant" ? "No" : "N/A",
    ]),
    styles: { fontSize: 8.5, cellPadding: 5, valign: "middle" },
    headStyles: { fillColor: [240, 242, 245], textColor: [90, 90, 90], fontStyle: "bold", fontSize: 7.5 },
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 110 },
      3: { cellWidth: 40, halign: "center" },
    },
    didParseCell: (data) => {
      if (data.section !== "body") return;
      if (data.column.index === 3) {
        const v = data.cell.raw;
        data.cell.styles.textColor = v === "No" ? [192, 57, 43] : v === "Sí" ? [39, 174, 96] : [130, 130, 130];
        data.cell.styles.fontStyle = "bold";
      }
      if (data.column.index === 1) {
        data.cell.styles.textColor = [26, 95, 160];
      }
    },
  });

  y = doc.lastAutoTable.finalY + 24;

  // ── Hallazgos y cierre (antes / después) ───────────────────────────────
  if (hallazgos.length) {
    if (y > pageH - 160) { doc.addPage(); y = margin; }
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.setTextColor(20, 40, 80);
    doc.text("Hallazgos y cierre (antes / después)", margin, y);
    y += 16;

    const cardH  = 108;
    const boxGap = 24;
    const boxW   = (contentW - 28 - boxGap) / 2;

    for (let i = 0; i < hallazgos.length; i++) {
      const h = hallazgos[i];
      if (y + cardH > pageH - 40) { doc.addPage(); y = margin; }

      doc.setFillColor(253, 236, 234);
      doc.roundedRect(margin + 4, y, contentW - 4, cardH, 4, 4, "F");
      doc.setDrawColor(192, 57, 43);
      doc.setLineWidth(2.2);
      doc.line(margin, y + 2, margin, y + cardH - 2);
      doc.setLineWidth(0.6);

      doc.setFontSize(7.5);
      doc.setFont(undefined, "bold");
      doc.setTextColor(192, 57, 43);
      doc.text((h.category || "General").toUpperCase(), margin + 14, y + 16);

      const open = h.status === "open";
      drawPill(doc, {
        x: margin + contentW - 50,
        y: y + 14,
        text: open ? "Abierto" : "Cerrado",
        color: open ? "#C0392B" : "#27AE60",
        fontSize: 7,
      });

      doc.setFontSize(9.5);
      doc.setFont(undefined, "bold");
      doc.setTextColor(30, 40, 60);
      const desc = doc.splitTextToSize(h.description || "-", contentW - 28)[0] || "-";
      doc.text(desc, margin + 14, y + 30);

      const boxY = y + 42;
      const boxH = cardH - 52;
      const box1X = margin + 14;
      const box2X = box1X + boxW + boxGap;

      doc.setFontSize(7);
      doc.setFont(undefined, "bold");
      doc.setTextColor(90, 90, 90);
      doc.text("ANTES", box1X, boxY - 4);
      doc.text("DESPUÉS", box2X, boxY - 4);

      doc.setDrawColor(210, 210, 210);
      if (doc.setLineDashPattern) doc.setLineDashPattern([2, 2], 0);
      doc.rect(box1X, boxY, boxW, boxH);
      doc.rect(box2X, boxY, boxW, boxH);
      if (doc.setLineDashPattern) doc.setLineDashPattern([], 0);

      doc.setDrawColor(150, 150, 150);
      doc.line(box1X + boxW + 6, boxY + boxH / 2, box2X - 6, boxY + boxH / 2);

      const photoDataUrl = hallazgoPhotos[i];
      if (photoDataUrl) {
        try {
          doc.addImage(photoDataUrl, "JPEG", box1X + 4, boxY + 4, boxW - 8, boxH - 8);
        } catch {
          doc.setFontSize(8);
          doc.setTextColor(160, 160, 160);
          doc.text("Sin foto", box1X + boxW / 2, boxY + boxH / 2, { align: "center" });
        }
      } else {
        doc.setFontSize(8);
        doc.setTextColor(160, 160, 160);
        doc.text("Sin foto", box1X + boxW / 2, boxY + boxH / 2, { align: "center" });
      }
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 160);
      doc.text("Pendiente", box2X + boxW / 2, boxY + boxH / 2, { align: "center" });

      y += cardH + 12;
    }
  }

  // ── Resumen + firma ─────────────────────────────────────────────────────
  if (y > pageH - 90) { doc.addPage(); y = margin; }
  y += 8;
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, pageW - margin, y);
  y += 20;

  doc.setFontSize(8.5);
  doc.setFont(undefined, "bold");
  doc.setTextColor(60, 60, 60);
  doc.text(`Resumen: ${compliant} conformes (Sí) · ${nonCompliant} no conformes (No) · ${na} N/A`, margin, y);
  doc.setFontSize(7.5);
  doc.setFont(undefined, "normal");
  doc.setTextColor(140, 140, 140);
  doc.text(`Generado por Sustemia · ${new Date().toLocaleString("es-AR")}`, margin, y + 12);

  if (inspection.signature?.image) {
    try {
      doc.addImage(inspection.signature.image, "PNG", pageW - margin - 110, y - 32, 110, 34);
    } catch { /* firma inválida — se sigue con la línea + nombre igual */ }
  }
  doc.setDrawColor(30, 30, 30);
  doc.line(pageW - margin - 110, y + 6, pageW - margin, y + 6);
  doc.setFontSize(9);
  doc.setFont(undefined, "bold");
  doc.setTextColor(20, 40, 80);
  doc.text(inspection.signature?.name || inspectorName || "-", pageW - margin, y + 17, { align: "right" });
  doc.setFontSize(7.5);
  doc.setFont(undefined, "normal");
  doc.setTextColor(120, 120, 120);
  doc.text(`${inspection.signature?.role || "Inspector"} — Firma`, pageW - margin, y + 27, { align: "right" });

  // ── Pie de página (todas las páginas) ──────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFontSize(7.5);
    doc.setFont(undefined, "normal");
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Documento generado electrónicamente por la plataforma Sustemia (SeH · Módulo Check List) · Registro ${registryId}`,
      pageW / 2, pageH - 20, { align: "center" }
    );
  }

  doc.save(`Informe ${inspection.template?.name || "inspeccion"} · ${registryId}.pdf`);
}
